// ===========================================
// Link Card (web links as class resources, 2026-09-07)
// ===========================================
// Same footprint as FileCard / FileDownloadCard, in the same grid. Opens the
// stored url in a new tab exactly as files do — never re-derived, never an
// inline player (Andrew D4).

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  OndemandVideo as VideoIcon,
} from '@mui/icons-material';
import { ClassLink, getLinkHostLabel, getLinkThumbnail } from '../../api/classLinks.api';
import { getVisibilityColor, getVisibilityLabel } from '../../api/resources.api';

export interface LinkCardProps {
  link: ClassLink;
  editable?: boolean;
  onEdit?: (link: ClassLink) => void;
  onDelete?: (link: ClassLink) => void;
  /** Show "Shared with {level}" when the link comes from the class's program level. */
  showProgramLevel?: boolean;
}

export default function LinkCard({ link, editable = false, onEdit, onDelete, showProgramLevel = true }: LinkCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const thumbnail = getLinkThumbnail(link);
  const host = getLinkHostLabel(link);

  const open = () => {
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      data-testid={`link-card-${link.id}`}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 3 } }}
    >
      <Box
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        role="link"
        tabIndex={0}
        aria-label={`Open ${link.title}`}
        sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', cursor: 'pointer', overflow: 'hidden' }}
      >
        {thumbnail ? (
          <Box component="img" src={thumbnail} alt={link.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : link.provider === 'VIMEO' ? (
          <VideoIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        ) : (
          <LinkIcon sx={{ fontSize: 48, color: 'primary.main' }} />
        )}
      </Box>

      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Tooltip title={link.url}>
          <Typography variant="subtitle2" noWrap sx={{ cursor: 'pointer' }} onClick={open}>
            {link.title}
          </Typography>
        </Tooltip>
        {link.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.5 }}
          >
            {link.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip label={host} size="small" variant="outlined" />
          {link.visibility !== 'ALL' && (
            <Chip label={getVisibilityLabel(link.visibility)} size="small" color={getVisibilityColor(link.visibility)} variant="outlined" />
          )}
        </Box>
        {showProgramLevel && link.programLevelId && link.ProgramLevel && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Shared with {link.ProgramLevel.name}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
        <Tooltip title="Open link">
          <IconButton size="small" onClick={open} aria-label={`Open ${link.title} in a new tab`}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {editable && (
          <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Link actions">
              <MoreIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { setAnchorEl(null); onEdit?.(link); }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); onDelete?.(link); }}>
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>Remove</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </CardActions>
    </Card>
  );
}
